import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import {
  DEALER_PROPS,
  REVIEWS_SALES,
  REVIEWS_SERVICE,
  REVIEWS_ALL,
  DEFAULT_REVIEWS_TYPE,
  REVIEWS_TYPES,
} from 'client/constants/dealer-reviews';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DealerInfoEntities } from 'client/data/models/dealer-info';
import { ReviewEntities, ReviewsModel, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { getOverallRating } from 'client/site-modules/dealerships/utils/dealer-rating';
import { ToggleButton } from 'site-modules/shared/components/inventory/toggle-button/toggle-button';
import { ToggleButtonGroup } from 'site-modules/shared/components/inventory/toggle-button-group/toggle-button-group';
import { DealerReviewsView } from 'site-modules/dealerships/components/dealer-reviews-view/dealer-reviews-view';

import './dealer-reviews.scss';

const DEFAULT_TRACKING = {
  PARENT: 'edm-entry-type-center',
  TYPE: 'type_select',
};

export function getFeaturedReview({ overallRating, reviewsType, reviewsByType }) {
  if (reviewsType !== REVIEWS_ALL) {
    return null; // only show the featured review for the all tab;
  }

  if (!(overallRating && overallRating.averageRating >= 3)) {
    return null; // don't show the featured review if the dealership has bad overall reviews
  }

  return reviewsByType && (reviewsByType[REVIEWS_SALES] || reviewsByType[REVIEWS_SERVICE]);
}

export class DealerReviewsComponent extends Component {
  constructor(props) {
    super(props);

    this.defaultReviewsType =
      props.reviewsByType && (props.reviewsByType[REVIEWS_SALES] || props.reviewsByType[REVIEWS_SERVICE])
        ? DEFAULT_REVIEWS_TYPE
        : REVIEWS_SALES;
  }

  /**
   * Switch reviews type by clicking on one of toggle buttons
   * @param e event
   * @param type current review type tab
   */
  switchReviewsType = (e, type) => {
    this.props.setModelValue(ReviewsPaths.getReviewsFilterPath(), ReviewsModel, {
      type,
      page: '1',
    });
  };

  render() {
    const { overallRatingByType, reviewsByType, className, tracking, params, isMobile, reviewsFilter, dealerInfo } =
      this.props;
    const { name } = this.props.dealerInfo;
    const reviewsType = get(reviewsFilter, 'type') || this.defaultReviewsType;
    const overallRating = getOverallRating(overallRatingByType, reviewsType);
    const hasReviews = !!(reviewsByType && (reviewsByType[REVIEWS_SALES] || reviewsByType[REVIEWS_SERVICE]));
    const featuredReview = getFeaturedReview({ overallRating, reviewsType, reviewsByType });

    // don't display the all tab if there are no reviews
    const tabsToDisplay = hasReviews ? REVIEWS_TYPES : [REVIEWS_SALES, REVIEWS_SERVICE];

    return (
      <Container
        className={classnames('dealer-reviews edm-entry container py-3 type-center container-narrow', className)}
        data-tracking-parent={get(tracking, 'parent') || DEFAULT_TRACKING.PARENT}
        id="edm-entry-dealer-reviews"
        tag="section"
      >
        <h2 className="size-30 fw-bold text-center mb-1 mb-md-2">Customer Reviews of {name}</h2>

        <div className="text-center">
          <p className="mb-0_5">Review type:</p>
          <ToggleButtonGroup
            className={classnames('review-type-toggle mb-2', { desktop: !isMobile })}
            onChange={this.switchReviewsType}
            value={reviewsType}
          >
            {tabsToDisplay.map(type => (
              <ToggleButton key={`mode_${type}`} value={type} className="dealer-review-type-switch-button">
                <span className="text-capitalize">{type}</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>

        <DealerReviewsView
          overallData={overallRating}
          featuredReview={featuredReview}
          reviewsType={reviewsType}
          params={params}
          showroomPage={get(dealerInfo, 'showroomPage')}
          isMobile={isMobile}
        />
      </Container>
    );
  }
}

DealerReviewsComponent.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  params: DEALER_PROPS.isRequired,
  dealerInfo: DealerInfoEntities.DealerInfo,
  overallRatingByType: PropTypes.shape({
    dealerId: PropTypes.string,
    salesReview: PropTypes.shape({
      averageRating: PropTypes.number,
      recommended: PropTypes.number,
      notRecommended: PropTypes.number,
      totalCount: PropTypes.number,
    }),
    serviceReview: PropTypes.shape({
      averageRating: PropTypes.number,
      recommended: PropTypes.number,
      notRecommended: PropTypes.number,
      totalCount: PropTypes.number,
    }),
  }),
  reviewsByType: PropTypes.shape({
    sales: PropTypes.object,
    service: PropTypes.object,
  }),
  reviewsFilter: ReviewEntities.ReviewsFilter,
  className: PropTypes.string,
  tracking: PropTypes.shape({
    parent: PropTypes.string,
    type: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
};

DealerReviewsComponent.defaultProps = {
  dealerInfo: null,
  overallRatingByType: null,
  reviewsByType: null,
  reviewsFilter: null,
  className: null,
  tracking: null,
  isMobile: false,
};

export const stateToPropsConfig = {
  overallRatingByType: bindToPath(
    ({ params: { dealerId } }) => ReviewsPaths.getOverallRatings({ dealerId }),
    ReviewsModel
  ),
  reviewsByType: bindToPath(({ params: { dealerId } }) => ReviewsPaths.getTopReviewsPath({ dealerId }), ReviewsModel),
  reviewsFilter: bindToPath(ReviewsPaths.getReviewsFilterPath, ReviewsModel),
};
export const mapStateToProps = state => ({ isMobile: state.mobile });

export const DealerReviews = connect(mapStateToProps)(connectToModel(DealerReviewsComponent, stateToPropsConfig));

DealerReviews.propTypes = {
  params: DEALER_PROPS.isRequired,
  dealerInfo: DealerInfoEntities.DealerInfo,
};
